/* ==========================================================================
   Rook UI Core — Portfolio Gallery Controller
   Web Component: <rk-portfolio-gallery-element>

   Project selection — clicking a title in the list (sidebar on desktop,
   horizontal scroller on mobile) swaps which project panel is visible.
   Only one project's title/description/grid is shown at a time. Scoped to
   `this` via the normal delegate() helper.

   Grid column count is NOT controlled here — it's fixed per breakpoint via
   the theme editor settings (mosaic_columns / mosaic_columns_mobile), baked
   into CSS variables at render time.

   Modal zoom/pan is wired separately, at module scope (see wireModalZoom
   below) rather than through this element's delegate(): the modal markup
   sits inside <rk-portal>, which relocates it to the end of <body> on load
   so its position:fixed overlay can escape the section's stacking context
   and render above header/footer. Once portaled it's no longer a descendant
   of <rk-portfolio-gallery-element>, so a listener scoped to `this` would
   never see clicks on it — hence the plain document-level listeners.
   ========================================================================== */

import { RkComponent } from '@rook/component';

const ZOOM_IMG_SELECTOR = '.rk-portfolio-gallery__modal .rk-modal__img';
const DRAG_THRESHOLD = 6;
const ZOOM_FACTOR = 2;
const IMG_ZOOMED = 'rk-portfolio-gallery__modal-img--zoomed';
const MEDIA_ZOOMED = 'rk-portfolio-gallery__modal-media--zoomed';

let modalZoomWired = false;
let drag = null;

function wireModalZoom() {
    if (modalZoomWired) return;
    modalZoomWired = true;

    document.addEventListener('pointerdown', (event) => {
        const target = event.target instanceof Element ? event.target.closest(ZOOM_IMG_SELECTOR) : null;
        if (!target) return;

        // Sem isso o navegador inicia o drag nativo da imagem (ghost image),
        // que rouba os pointermove e mata o pan.
        event.preventDefault();

        target.setPointerCapture(event.pointerId);
        const container = target.closest('.rk-modal__media--image');
        drag = {
            target,
            container,
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startScrollLeft: container ? container.scrollLeft : 0,
            startScrollTop: container ? container.scrollTop : 0,
            moved: false,
        };
    }, true);

    document.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.pointerId) return;

        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;

        if (!drag.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
            drag.moved = true;
            drag.target.classList.add('rk-portfolio-gallery__modal-img--dragging');
        }

        if (drag.moved && drag.container) {
            drag.container.scrollLeft = drag.startScrollLeft - dx;
            drag.container.scrollTop = drag.startScrollTop - dy;
        }
    }, true);

    const endDrag = (event) => {
        if (!drag || event.pointerId !== drag.pointerId) return;

        const { target, container, moved } = drag;
        drag = null;
        target.classList.remove('rk-portfolio-gallery__modal-img--dragging');

        // A genuine tap/click (no meaningful pointer movement) toggles zoom.
        // A drag (used to pan a zoomed image) must NOT also toggle it off.
        if (moved) return;

        if (!target.classList.contains(IMG_ZOOMED)) {
            zoomIn(target, container, event.clientX, event.clientY);
        } else {
            zoomOut(target, container);
        }
    };

    document.addEventListener('pointerup', endDrag, true);
    document.addEventListener('pointercancel', endDrag, true);

    document.addEventListener('rk:modal:close', (event) => {
        const scope = event.target;
        if (!(scope instanceof Element)) return;

        scope.querySelectorAll(`.${IMG_ZOOMED}`).forEach((img) => {
            zoomOut(img, img.closest('.rk-modal__media--image'));
        });
    });
}

/**
 * Amplia a imagem em ZOOM_FACTOR sobre o tamanho "fitted" atual e ajusta o
 * scroll do container pra manter o ponto clicado embaixo do cursor —
 * zoom-to-point, em vez de sempre pular pro canto superior esquerdo.
 * A largura vai inline em px (não em % do container) pra que o fator de
 * zoom seja o mesmo pra retrato e paisagem.
 */
function zoomIn(target, container, clientX, clientY) {
    const rect = target.getBoundingClientRect();
    const relX = (clientX - rect.left) / rect.width;
    const relY = (clientY - rect.top) / rect.height;

    target.style.width = `${Math.round(rect.width * ZOOM_FACTOR)}px`;
    target.classList.add(IMG_ZOOMED);
    container?.classList.add(MEDIA_ZOOMED);

    if (!container) return;

    const zoomedRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    container.scrollLeft = relX * zoomedRect.width - (clientX - containerRect.left);
    container.scrollTop = relY * zoomedRect.height - (clientY - containerRect.top);
}

function zoomOut(target, container) {
    target.style.width = '';
    target.classList.remove(IMG_ZOOMED);
    container?.classList.remove(MEDIA_ZOOMED);
    if (container) {
        container.scrollLeft = 0;
        container.scrollTop = 0;
    }
}

class RkPortfolioGalleryElement extends RkComponent {
    static hydration = 'idle';

    setup() {
        wireModalZoom();

        this.delegate({
            'click [data-project-select]': this.onProjectSelect,
        });
    }

    onProjectSelect(event, target) {
        const index = target.dataset.projectSelect;

        this.querySelectorAll('[data-project-select]').forEach((btn) => {
            const isActive = btn.dataset.projectSelect === index;
            btn.classList.toggle('rk-portfolio-gallery__index-item--active', isActive);
            btn.setAttribute('aria-selected', String(isActive));
        });

        this.querySelectorAll('[data-project-panel]').forEach((panel) => {
            panel.classList.toggle(
                'rk-portfolio-gallery__project-panel--active',
                panel.dataset.projectPanel === index
            );
        });
    }
}

if (!customElements.get('rk-portfolio-gallery-element')) {
    customElements.define('rk-portfolio-gallery-element', RkPortfolioGalleryElement);
}
